import type { Control, FieldPath, FieldValues } from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { FormTooltip } from "@/components/ui/form-description";

interface TextareaFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<React.ComponentProps<typeof Textarea>, "name" | "defaultValue"> {
  control: Control<TFieldValues>;
  name: TName;
  label: string;
  description?: string;
  descriptionAsTooltip?: boolean;
  required?: boolean;
  requiredLabel?: string;
  showCharacterCount?: boolean;
  maxLength?: number;
  visuallyHiddenLabel?: boolean;
  characterCountSuffix?: string;
}

export function TextareaField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  description,
  descriptionAsTooltip = false,
  required = false,
  requiredLabel = "Required",
  showCharacterCount = false,
  maxLength,
  visuallyHiddenLabel = false,
  characterCountSuffix = "characters",
  className,
  ...textareaProps
}: Readonly<TextareaFieldProps<TFieldValues, TName>>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const currentLength = (field.value as string)?.length || 0;
        const characterCountText = maxLength
          ? `${currentLength}/${maxLength} ${characterCountSuffix}`
          : `${currentLength} ${characterCountSuffix}`;

        return (
          <FormItem className={className}>
            <div className={cn(visuallyHiddenLabel ? "sr-only" : "")}>
              <FormLabel className="inline-block leading-snug">
                {label}
                {required ? (
                  <>
                    <span className="ms-1 text-red-500" aria-hidden="true">
                      *
                    </span>
                    <span className="sr-only">({requiredLabel})</span>
                  </>
                ) : null}
              </FormLabel>
              {description && !descriptionAsTooltip ? (
                <FormDescription>{description}</FormDescription>
              ) : null}
              {description && descriptionAsTooltip ? (
                <FormTooltip description={description} />
              ) : null}
            </div>
            <FormControl>
              <Textarea
                {...field}
                {...textareaProps}
                aria-required={required ? "true" : "false"}
                maxLength={maxLength}
              />
            </FormControl>
            {showCharacterCount ? (
              <div className="text-sm text-muted-foreground">
                {characterCountText}
              </div>
            ) : null}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
